import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { getLocalStorage, clearLocalStorages } from '@/utils/storages';
import { SERVICES } from '@/configs';

type AdminParams = {
  username: string;
  password?: string;
  level: string;
  method: string;
};

const queryKeyAdd = 'add-admin';

// Helper function to handle 401 errors
const handle401Error = (error: any) => {
  if (error?.response?.status === 401 || error?.response?.data?.code === 401) {
    clearLocalStorages();
    window.location.href = '/';
    return true;
  }
  return false;
};

export function useAdmin() {
  const accessToken = getLocalStorage('access_token');

  return useQuery({
    queryKey: ['admin-list'],
    queryFn: async () => {
      try {
        const res = await axios.get(SERVICES.ADMIN, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        return res?.data?.data;
      } catch (error: any) {
        if (handle401Error(error)) {
          throw new Error('Unauthorized');
        }
        throw error;
      }
    },
    enabled: !!accessToken,
  });
};

async function fetchAddAdmin(adminParams: AdminParams): Promise<string> {
  const accessToken = getLocalStorage('access_token');

  if (adminParams.username === '' || adminParams.level === '') {
    throw new Error('Username or level is empty');
  }

  const { method, password, ...rest } = adminParams;
  let payload: any = { ...rest };
  if (method !== 'PUT' || (method === 'PUT' && password)) {
    payload.password = password;
  }

  try {
    const options = {
      method,
      url: SERVICES.ADMIN,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      data: payload,
    };

    const res = await axios(options);

    if (res?.data?.code === 200) {
      return res?.data?.code;
    }
    if (res?.data?.code === 401) {
      clearLocalStorages();
      window.location.href = '/';
      throw new Error('Unauthorized');
    }
    throw new Error(res?.data?.code);
  } catch (error: any) {
    if (handle401Error(error)) {
      throw new Error('Unauthorized');
    }
    throw error;
  }
}


export function useAddAmin(adminParams: AdminParams) {
  const queryClient = useQueryClient();
  const queryKey = ['add-admin'];

  const query = useQuery({
    queryKey,
    queryFn: async () => await fetchAddAdmin(adminParams),
    enabled: false,
  });

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey });
    };
  }, [queryClient]);

  return query;
}

export function useDeleteAdmin() {
  const accessToken = getLocalStorage('access_token');

  return useMutation({
    mutationFn: async (username: string) => {
      try {
        const res = await axios.delete(SERVICES.ADMIN, {
          params: { username },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        return res?.data;
      } catch (error: any) {
        if (handle401Error(error)) {
          throw new Error('Unauthorized');
        }
        throw error;
      }
    },
  });
}
